"use client";

import Link from "next/link";
import type { ReactNode } from "react";
import { buttonClass } from "@/components/ui/button";
import { useAnalytics } from "@/lib/analytics/hooks";
import { SHORTLIST_SLA_HOURS } from "@/lib/billing/shortlist-offer";
import {
	type IntelClickCta,
	type IntelClickSurface,
	intelSeeRolesHref,
	intelShortlistHref,
} from "@/lib/insights/intel-funnel";

/**
 * Client-side tracking for intel surfaces. Every click goes through the
 * existing `useAnalytics().trackJobClick` — `job_card_clicked` in PostHog plus
 * the D1 interaction beacon when a real job id exists — so the intel->job CTR
 * sits in the same identity population as its `intel_module_viewed`
 * denominator (both are client captures; see docs/claude/telemetry.md §2b).
 */
function useIntelClick(surface: IntelClickSurface, locale: string) {
	const { trackJobClick } = useAnalytics();
	return (
		jobId: string | null,
		cta: IntelClickCta,
		extra: Record<string, unknown> = {},
	) => {
		try {
			trackJobClick(jobId, { surface, cta, locale, ...extra });
		} catch {
			// analytics must never break navigation
		}
	};
}

/** A job-detail link on an intel surface. `href` must come from `buildJobUrl`. */
export function IntelTrackedJobLink({
	href,
	jobId,
	surface,
	locale,
	placement,
	className,
	children,
}: {
	href: string;
	jobId: string;
	surface: IntelClickSurface;
	locale: string;
	placement: string;
	className?: string;
	children: ReactNode;
}) {
	const track = useIntelClick(surface, locale);
	return (
		<Link
			href={href}
			onClick={() => track(jobId, "job", { placement })}
			className={className}
		>
			{children}
		</Link>
	);
}

/**
 * A tracked link from an intel surface to a job LIST in another zone (e.g. a
 * report's company row → that company's page on the apex app). Plain `<a>`
 * for the same zone-boundary reason as `IntelFunnelCta` below; `jobId` is
 * null, so only the PostHog `job_card_clicked` fires.
 */
export function IntelTrackedListLink({
	href,
	surface,
	locale,
	placement,
	className,
	children,
}: {
	href: string;
	surface: IntelClickSurface;
	locale: string;
	placement: string;
	className?: string;
	children: ReactNode;
}) {
	const track = useIntelClick(surface, locale);
	return (
		<a
			href={href}
			onClick={() => track(null, "see_roles", { placement })}
			className={className}
		>
			{children}
		</a>
	);
}

/**
 * Funnel CTAs for pages that sell insight, not listings (market-pulse posts,
 * research reports): one seeker path per skill into the filtered feed, and one
 * employer path into the shortlist offer. Plain `<a>` on purpose — both
 * targets live in other zones (apex feed, employers zone), where a client-side
 * `<Link>` transition would 404 instead of crossing the zone boundary.
 */
export function IntelFunnelCta({
	surface,
	locale,
	skills,
	sourceSlug,
}: {
	surface: IntelClickSurface;
	locale: string;
	skills: string[];
	/** Which post/report the click came from, for per-page breakdowns. */
	sourceSlug: string;
}) {
	const track = useIntelClick(surface, locale);
	const isId = locale === "id";
	const shown = skills.slice(0, 3);

	return (
		<aside
			aria-labelledby={`intel-cta-${surface}`}
			data-testid="intel-funnel-cta"
			className="not-prose mt-10 rounded-2xl border border-border bg-card p-5 sm:p-6"
		>
			<h2
				id={`intel-cta-${surface}`}
				className="text-base font-semibold text-foreground"
			>
				{isId ? "Dari data ke langkah berikutnya" : "Put this data to work"}
			</h2>

			{shown.length > 0 && (
				<div className="mt-3">
					<p className="text-sm text-muted-foreground">
						{isId
							? "Lihat lowongan yang sedang dibuka untuk skill ini:"
							: "See open roles for these skills:"}
					</p>
					<ul className="mt-2 flex flex-wrap gap-2">
						{shown.map((skill) => (
							<li key={skill}>
								<a
									href={intelSeeRolesHref(locale, skill)}
									onClick={() =>
										track(null, "see_roles", {
											skill,
											source_slug: sourceSlug,
										})
									}
									className={buttonClass({
										variant: "secondary",
										size: "sm",
										shape: "pill",
									})}
								>
									{isId
										? `Lowongan ${skill} →`
										: `See open roles for ${skill} →`}
								</a>
							</li>
						))}
					</ul>
				</div>
			)}

			<div className="mt-5 border-t border-border pt-4">
				<p className="text-sm text-muted-foreground">
					{isId
						? `Sedang merekrut? Dapatkan shortlist kandidat terverifikasi dalam ${SHORTLIST_SLA_HOURS} jam.`
						: `Hiring? Get a verified candidate shortlist in ${SHORTLIST_SLA_HOURS} hours.`}
				</p>
				<a
					href={intelShortlistHref(locale, surface)}
					onClick={() =>
						track(null, "employer_shortlist", { source_slug: sourceSlug })
					}
					className={buttonClass({ size: "sm", className: "mt-3" })}
				>
					{isId ? "Minta shortlist →" : "Get a shortlist →"}
				</a>
			</div>
		</aside>
	);
}
